import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, UploadCloud, Link2, KeyRound, Trash2, Star } from "lucide-react";
import { adminApi, formatApiErrorDetail } from "../../lib/adminApi";
import { uploadGalleryPhoto } from "../../lib/chunkedUpload";
import { galleryLink } from "./AdminGalleries";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const miniBtn = "inline-flex items-center gap-1.5 border px-2.5 py-1.5 text-[9px] font-mono tracking-[0.15em] uppercase transition-colors";

export default function GalleryManager({ gallery, onBack, onUpdated }) {
  const [photos, setPhotos] = useState([]);
  const [progress, setProgress] = useState(null);
  const [passcode, setPasscode] = useState("");
  const [confirmId, setConfirmId] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await adminApi.get(`/galleries/${gallery.id}/photos`);
      setPhotos(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  useEffect(() => { load(); }, [gallery.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const upload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    let done = 0;
    for (const file of files) {
      setProgress({ done, total: files.length, name: file.name, pct: 0 });
      try {
        const photo = await uploadGalleryPhoto(gallery.id, file, (pct) => setProgress((p) => ({ ...p, pct })));
        setPhotos((list) => [...list, photo]);
        done += 1;
      } catch (err) {
        toast.error(`${file.name}: ${formatApiErrorDetail(err.response?.data?.detail)}`);
      }
    }
    setProgress(null);
    if (fileRef.current) fileRef.current.value = "";
    if (done) {
      toast.success(`${done} photo${done > 1 ? "s" : ""} added`);
      onUpdated({ ...gallery, photo_count: photos.length + done });
    }
  };

  const remove = async (id) => {
    try {
      await adminApi.delete(`/galleries/${gallery.id}/photos/${id}`);
      setPhotos((list) => list.filter((p) => p.id !== id));
      setConfirmId(null);
      toast.success("Photo removed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const setCover = async (id) => {
    try {
      const { data } = await adminApi.patch(`/galleries/${gallery.id}`, { cover_photo_id: id });
      onUpdated(data);
      toast.success("Cover updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const changePasscode = async (e) => {
    e.preventDefault();
    if (passcode.length < 4) return toast.error("Passcode needs at least 4 characters");
    try {
      const { data } = await adminApi.patch(`/galleries/${gallery.id}`, { passcode });
      onUpdated(data);
      setPasscode("");
      toast.success("Passcode updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const copyLink = async () => {
    await navigator.clipboard.writeText(galleryLink(gallery.slug));
    toast.success("Gallery link copied");
  };

  return (
    <div data-testid="gallery-manager">
      <button type="button" data-testid="gallery-manager-back" onClick={onBack} className="inline-flex items-center gap-2 text-[10px] font-mono tracking-[0.2em] uppercase text-stone-400 hover:text-champagne transition-colors"><ArrowLeft size={12} /> All galleries</button>
      <div className="mt-4 flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="text-[10px] font-mono tracking-[0.25em] uppercase text-champagne">{gallery.couple || "Client gallery"}</p>
          <h2 className="mt-1 font-serif text-2xl text-ivory">{gallery.title}</h2>
          <p className="mt-2 text-[10px] font-mono text-stone-500">{galleryLink(gallery.slug)}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button data-testid="gallery-manager-copy" onClick={copyLink} className={`${miniBtn} border-white/15 text-stone-300 hover:border-champagne hover:text-champagne py-2.5 px-4`}><Link2 size={12} /> Copy Link</button>
          <a data-testid="gallery-manager-open" href={`/gallery/${gallery.slug}`} target="_blank" rel="noreferrer" className={`${miniBtn} border-white/15 text-stone-300 hover:border-champagne hover:text-champagne py-2.5 px-4`}>Open as Couple</a>
        </div>
      </div>

      <div className="mt-8 grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-4 space-y-6">
          <label htmlFor="gallery-files" data-testid="gallery-dropzone" className="flex flex-col items-center justify-center gap-2 border border-dashed border-white/20 hover:border-champagne/60 bg-elevated p-8 cursor-pointer transition-colors text-center">
            <UploadCloud size={22} className="text-champagne" />
            <span className="text-xs font-light text-stone-300">{progress ? `Uploading ${progress.done + 1} / ${progress.total} — ${Math.round(progress.pct * 100)}%` : "Add full-resolution photos"}</span>
            <span className="text-[9px] font-mono tracking-[0.2em] uppercase text-stone-600">JPEG / PNG / WebP · up to 60MB each · originals kept</span>
            <input id="gallery-files" ref={fileRef} data-testid="gallery-file-input" type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" disabled={!!progress} onChange={upload} />
          </label>
          {progress && (
            <div className="h-[2px] bg-white/10" data-testid="gallery-upload-progress">
              <div className="h-full bg-champagne transition-all duration-300" style={{ width: `${((progress.done + progress.pct) / progress.total) * 100}%` }} />
            </div>
          )}
          <form onSubmit={changePasscode} className="border border-white/10 bg-elevated p-5">
            <p className="text-[9px] font-mono tracking-[0.25em] uppercase text-stone-500 inline-flex items-center gap-2"><KeyRound size={11} /> Change passcode</p>
            <div className="mt-3 flex gap-2">
              <input data-testid="gallery-passcode-new" value={passcode} onChange={(e) => setPasscode(e.target.value)} placeholder="New passcode" className="flex-1 bg-transparent border-b border-white/15 focus:border-champagne outline-none py-2 text-sm font-light text-ivory placeholder:text-stone-600" />
              <button type="submit" data-testid="gallery-passcode-save" className={`${miniBtn} border-champagne text-champagne hover:bg-champagne hover:text-obsidian`}>Save</button>
            </div>
          </form>
        </div>

        <div className="lg:col-span-8">
          {photos.length === 0 ? (
            <p data-testid="gallery-photos-empty" className="font-serif text-xl italic text-stone-500">No photos yet — add the couple's frames.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="gallery-photos-grid">
              {photos.map((p) => {
                const isCover = gallery.cover_photo_id === p.id;
                return (
                  <div key={p.id} data-testid={`gallery-photo-${p.id}`} className={`group relative aspect-square overflow-hidden bg-surface border ${isCover ? "border-gold" : "border-white/10"}`}>
                    <img src={`${BACKEND}${p.url}`} alt={p.filename} loading="lazy" className="h-full w-full object-cover" />
                    {isCover && <span className="absolute top-1.5 left-1.5 bg-gold text-obsidian px-1.5 py-0.5 text-[8px] font-mono tracking-[0.2em] uppercase">Cover</span>}
                    <div className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-1 bg-black/70 p-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      <span className="truncate text-[9px] font-mono text-stone-300">{p.filename}</span>
                      <div className="flex gap-1 shrink-0">
                        {!isCover && <button data-testid={`gallery-photo-cover-${p.id}`} onClick={() => setCover(p.id)} className="p-1 text-stone-300 hover:text-gold" aria-label="Set as cover"><Star size={11} /></button>}
                        {confirmId === p.id ? (
                          <button data-testid={`gallery-photo-delete-confirm-${p.id}`} onClick={() => remove(p.id)} className="px-1.5 text-[8px] font-mono uppercase text-terracotta">Confirm</button>
                        ) : (
                          <button data-testid={`gallery-photo-delete-${p.id}`} onClick={() => setConfirmId(p.id)} className="p-1 text-stone-300 hover:text-terracotta" aria-label="Delete"><Trash2 size={11} /></button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
